import Table from './table/Table';

const TOTAL_KEY = '__total__';

/**
 * Renders respondents + answers inside an existing InfoPopover modal.
 *
 * With one (or zero) measure moments it shows two summary rows; with more than
 * one it shows a per-moment breakdown table (Meetmoment / Respondenten /
 * Antwoorden) plus a Totaal row. Respondents are unique people; answers are the
 * raw response records (higher than respondents for multiple-choice / matrix /
 * paragraph questions).
 *
 * @param {object} props
 * @param {number} props.respondents - Total unique respondents.
 * @param {number} props.answers - Total answers (response records).
 * @param {import('../../helpers/dataHelpers/summarizeRespondentsByMoment').MomentResponseSummary[]} [props.perMoment] - Per-moment breakdown.
 * @param {boolean} [props.loading] - The records are still being fetched: say so instead of showing 0.
 * @param {Error|null} [props.error] - The fetch failed: say so instead of showing 0.
 */
const ResponsesSummarySection = ({ respondents, answers, perMoment = [], loading = false, error = null }) => {
    // A 0 while the records are on their way (or failed) reads as "nobody
    // answered". AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (error) {
        return <p className="info-popover-row">Het aantal respondenten kon niet worden geladen.</p>;
    }
    if (loading) {
        return <p className="info-popover-row">Respondenten worden geteld…</p>;
    }

    const showPerMoment = Array.isArray(perMoment) && perMoment.length > 1;

    if (!showPerMoment) {
        return (
            <>
                <div className="info-popover-row">
                    <span className="info-popover-row-label">Respondenten</span>
                    <span className="info-popover-row-value">{respondents}</span>
                </div>
                <div className="info-popover-row">
                    <span className="info-popover-row-label">Antwoorden</span>
                    <span className="info-popover-row-value">{answers}</span>
                </div>
            </>
        );
    }

    const items = [
        ...perMoment.map((m) => ({ ...m, key: `${m.position}`, isTotal: false })),
        { label: 'Totaal', respondents, answers, key: TOTAL_KEY, isTotal: true },
    ];

    const columns = [
        { key: 'label', label: 'Meetmoment', searchable: false },
        { key: 'respondents', label: 'Respondenten', searchable: false },
        { key: 'answers', label: 'Antwoorden', searchable: false },
    ];

    return (
        <div className="info-popover-section">
            <p className="info-popover-chart-title">Respondenten per meetmoment</p>
            <Table
                columns={columns}
                items={items}
                getItemKey={(item) => item.key}
                showSearch={false}
                getRowClassName={(item) => (item.isTotal ? 'projects-breakdown-section__total-row' : '')}
                renderRow={(item) => (
                    <>
                        <td>{item.label}</td>
                        <td>{item.respondents}</td>
                        <td>{item.answers}</td>
                    </>
                )}
            />
        </div>
    );
};

export default ResponsesSummarySection;
